import {
  trackToolExperience,
  deviceLayout,
} from "../features/toolExperience/telemetry";
import { Home, Grid2X2, Plus, Search, Moon, Keyboard } from "lucide-react";
import { getAvailableTools, canStartTool } from "../features/tools/registry";
import { useAuth } from "../contexts/AuthContext";
import { useToolScope } from "../features/toolExperience/useToolScope";
import { scopeKey } from "../features/toolExperience/types";
import { readStored, writeStored } from "../features/toolExperience/storage";
import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  createElement,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "./useTheme";
import { useToast } from "./useToast";

import { logger } from "../utils/logger";

const OPEN_SEARCH_EVENT = "lazysem:open-search";

export function openCommandPalette() {
  window.dispatchEvent(new Event(OPEN_SEARCH_EVENT));
}

interface Command {
  id: string;
  title: string;
  description?: string;
  icon?: ReactNode;
  category: "navigation" | "action" | "search" | "help";
  keywords: string[];
  action: () => void;
  shortcut?: string;
}

interface RecentItem {
  commandId: string;
  title: string;
  timestamp: string;
}

interface CommandWithScore extends Command {
  score: number;
}

export function useCommandPalette() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const scope = useToolScope();
  const recentKey = `lazysem_command_recent_v1:${scopeKey(scope)}`;
  const { toggleTheme } = useTheme();
  const { success } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  useEffect(() => {
    const open = () => {
      setQuery("");
      setSelectedIndex(0);
      setIsOpen(true);
    };
    window.addEventListener(OPEN_SEARCH_EVENT, open);
    return () => window.removeEventListener(OPEN_SEARCH_EVENT, open);
  }, []);
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  useEffect(() => {
    const saved = readStored(recentKey, true);
    setRecentItems(
      Array.isArray(saved)
        ? saved
            .filter(
              (i): i is RecentItem =>
                !!i &&
                typeof i.commandId === "string" &&
                typeof i.title === "string",
            )
            .slice(0, 10)
        : [],
    );
  }, [recentKey]);
  const commands = useMemo<Command[]>(() => {
    const tools = getAvailableTools(user?.role);
    return [
      {
        id: "nav-dashboard",
        title: "Go to Home",
        description: "Continue your work",
        icon: createElement(Home, { size: 20 }),
        category: "navigation",
        keywords: ["home", "dashboard"],
        action: () => navigate("/dashboard"),
      },
      {
        id: "nav-tools",
        title: "All tools",
        description: "Find and pin tools",
        icon: createElement(Grid2X2, { size: 20 }),
        category: "navigation",
        keywords: ["tools", "catalog", "discover"],
        action: () => navigate("/tools"),
      },
      ...tools.map((t) => ({
        id: `nav-${t.id}`,
        title: `Open ${t.label}`,
        description: t.description,
        icon: createElement(t.icon, { size: 20 }),
        category: "navigation" as const,
        keywords: [t.label, ...t.keywords],
        action: () => {
          trackToolExperience("tool_discovered", {
            toolId: t.id,
            source: "command",
            layout: deviceLayout(),
          });
          navigate(t.path);
        },
      })),
      ...tools
        .filter((t) => canStartTool(t, user?.role))
        .map((t) => ({
          id: `start-${t.id}`,
          title: t.startAction!.label,
          description: t.description,
          icon: createElement(Plus, { size: 20 }),
          category: "action" as const,
          keywords: ["new", "start", ...t.keywords],
          action: () => navigate(t.startAction!.href),
        })),
      ...tools
        .filter((t) =>
          ["briefings", "monitoring", "similarity", "clustering"].includes(
            t.id,
          ),
        )
        .map((t) => ({
          id: `search-${t.id}`,
          title: `Search ${t.label}`,
          icon: createElement(Search, { size: 20 }),
          category: "search" as const,
          keywords: ["search", "find", ...t.keywords],
          action: () => navigate(`${t.path}?focus=search&view=projects&q=`),
        })),
      {
        id: "action-toggle-theme",
        title: "Toggle dark mode",
        icon: createElement(Moon, { size: 20 }),
        category: "action",
        keywords: ["theme", "dark", "light"],
        action: toggleTheme,
      },
      {
        id: "help-shortcuts",
        title: "Keyboard shortcuts",
        icon: createElement(Keyboard, { size: 20 }),
        category: "help",
        keywords: ["help", "keyboard", "shortcuts"],
        action: () =>
          success(
            "Keyboard shortcuts",
            "Ctrl / ⌘ K opens search. Use arrows and Enter to select a command; Escape closes it.",
          ),
      },
    ];
  }, [navigate, toggleTheme, success, user?.role]);

  const fuzzyMatch = useCallback((text: string, query: string): number => {
    if (!query) return 0;

    const queryLower = query.toLowerCase();
    const textLower = text.toLowerCase();

    // Exact match gets highest priority
    if (textLower.includes(queryLower)) {
      const exactIndex = textLower.indexOf(queryLower);
      return 100 - exactIndex; // Earlier matches score higher
    }

    // Fuzzy matching
    let score = 0;
    let queryIndex = 0;
    let lastMatchIndex = -1;

    for (
      let i = 0;
      i < textLower.length && queryIndex < queryLower.length;
      i++
    ) {
      if (textLower[i] === queryLower[queryIndex]) {
        score += 10;

        // Bonus for consecutive matches
        if (i === lastMatchIndex + 1) {
          score += 5;
        }

        lastMatchIndex = i;
        queryIndex++;
      }
    }

    // Bonus for complete matches
    if (queryIndex === queryLower.length) {
      score += 20;
    }

    return queryIndex === queryLower.length ? score : 0;
  }, []);

  const filteredCommands = useMemo<CommandWithScore[]>(() => {
    if (!query) {
      // Show recent items first when no query
      const recentCommandIds = recentItems.map((item) => item.commandId);
      const recentCommands = commands.filter((cmd) =>
        recentCommandIds.includes(cmd.id),
      );
      const otherCommands = commands.filter(
        (cmd) => !recentCommandIds.includes(cmd.id),
      );

      return [...recentCommands, ...otherCommands].map((cmd) => ({
        ...cmd,
        score: 0,
      }));
    }

    return commands
      .map((command) => ({
        ...command,
        score: Math.max(
          fuzzyMatch(command.title, query),
          fuzzyMatch(command.description || "", query),
          ...command.keywords.map((keyword) => fuzzyMatch(keyword, query)),
        ),
      }))
      .filter((command) => command.score > 0)
      .sort((a, b) => {
        // Sort by score first, then by category priority
        if (a.score !== b.score) {
          return b.score - a.score;
        }

        const categoryPriority = {
          navigation: 4,
          action: 3,
          search: 2,
          help: 1,
        };

        return categoryPriority[b.category] - categoryPriority[a.category];
      });
  }, [commands, query, recentItems, fuzzyMatch]);

  const groupedCommands = useMemo(() => {
    const groups: Record<string, CommandWithScore[]> = {};

    filteredCommands.forEach((command) => {
      const category = command.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(command);
    });

    return groups;
  }, [filteredCommands]);

  const addToRecent = useCallback(
    (command: Command) => {
      setRecentItems((prev) => {
        const filtered = prev.filter((item) => item.commandId !== command.id);
        const newRecent = [
          {
            commandId: command.id,
            title: command.title,
            timestamp: new Date().toISOString(),
          },
          ...filtered,
        ].slice(0, 10); // Keep only 10 recent items

        writeStored(recentKey, newRecent, true);
        return newRecent;
      });
    },
    [recentKey],
  );

  const executeCommand = useCallback(
    (command: Command) => {
      try {
        command.action();
        addToRecent(command);
        setIsOpen(false);
        setQuery("");
        setSelectedIndex(0);
      } catch (error) {
        logger.error("Failed to execute command:", error);
      }
    },
    [addToRecent],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Global shortcut to open command palette
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(true);
        return;
      }

      // Individual command shortcuts
      if (e.metaKey || e.ctrlKey) {
        const shortcutCommand = commands.find((cmd) => {
          if (!cmd.shortcut) return false;

          const shortcutKeys = cmd.shortcut.toLowerCase().split("+");
          const hasShift =
            shortcutKeys.includes("⇧") || shortcutKeys.includes("shift");
          const key = shortcutKeys[shortcutKeys.length - 1];

          return e.key.toLowerCase() === key && e.shiftKey === hasShift;
        });

        if (shortcutCommand) {
          e.preventDefault();
          executeCommand(shortcutCommand);
          return;
        }
      }

      // Command palette navigation
      if (isOpen) {
        if (e.key === "Escape") {
          setIsOpen(false);
          setQuery("");
          setSelectedIndex(0);
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIndex((prev) =>
            Math.min(prev + 1, filteredCommands.length - 1),
          );
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === "Enter") {
          e.preventDefault();
          const selectedCommand = filteredCommands[selectedIndex];
          if (selectedCommand) {
            executeCommand(selectedCommand);
          }
        }
      }
    },
    [isOpen, filteredCommands, selectedIndex, commands, executeCommand],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const getCategoryIcon = () => createElement(Search, { size: 16 });

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "navigation":
        return "Navigation";
      case "action":
        return "Actions";
      case "search":
        return "Search";
      case "help":
        return "Help";
      default:
        return "Commands";
    }
  };

  return {
    isOpen,
    setIsOpen,
    query,
    setQuery,
    selectedIndex,
    setSelectedIndex,
    filteredCommands,
    groupedCommands,
    recentItems,
    executeCommand,
    getCategoryIcon,
    getCategoryLabel,
  };
}
